import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, Users, Star, BookOpen, Play, Bookmark, 
  CheckCircle, ArrowRight, User, Calendar 
} from 'lucide-react';
import Button from '../../../components/ui/Button';

const TutorialCard = ({ 
  tutorial, 
  onStart, 
  onBookmark, 
  isBookmarked = false,
  userProgress,
  featured = false 
}) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-blue-600 bg-blue-100';
      case 'Advanced': return 'text-purple-600 bg-purple-100';
      case 'Expert': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const progressPercentage = userProgress?.completedLessons / tutorial?.lessons * 100 || 0;

  return (
    <motion.div
      className={`bg-card border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg group ${
        featured ? 'border-yellow-300' : 'border-border hover:border-blue-300'
      }`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -5 }}
    >
      {/* Thumbnail */}
      <div className="relative h-48 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-gray-800 dark:to-gray-700">
        {/* Featured Badge */}
        {featured && (
          <div className="absolute top-3 right-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
            <Star className="w-3 h-3" />
            <span>Featured</span>
          </div>
        )}
        
        {/* Bookmark Button */}
        <button
          onClick={(e) => {
            e?.stopPropagation();
            onBookmark(tutorial?.id);
          }}
          className={`absolute top-3 left-3 p-2 rounded-full transition-colors ${
            isBookmarked ? 'bg-blue-500 text-white' : 'bg-white/90 text-gray-600 hover:bg-white'
          }`}
        >
          <Bookmark className="w-4 h-4" />
        </button>

        {/* Category Badge */}
        <div className="absolute bottom-3 left-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
          {tutorial?.category}
        </div>

        {/* Play Icon Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
            <Play className="w-8 h-8 text-blue-600 ml-1" />
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(tutorial?.difficulty)}`}>
              {tutorial?.difficulty}
            </span>
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <Star className="w-3 h-3 text-yellow-500" />
              <span>{tutorial?.rating}</span>
            </div>
          </div>
          
          <h3 className="text-lg font-bold mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
            {tutorial?.title}
          </h3>
          
          <p className="text-muted-foreground text-sm line-clamp-3 mb-3">
            {tutorial?.description}
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-4 py-3 border-t border-b border-border">
          <div className="text-center">
            <div className="text-sm font-semibold flex items-center justify-center space-x-1">
              <BookOpen className="w-3 h-3" />
              <span>{tutorial?.lessons}</span>
            </div>
            <div className="text-xs text-muted-foreground">Lessons</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold flex items-center justify-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{tutorial?.estimatedTime}</span>
            </div>
            <div className="text-xs text-muted-foreground">Duration</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold flex items-center justify-center space-x-1">
              <Users className="w-3 h-3" />
              <span>{tutorial?.enrolled?.toLocaleString()}</span>
            </div>
            <div className="text-xs text-muted-foreground">Enrolled</div>
          </div>
        </div>

        {/* Tags */}
        {tutorial?.tags && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {tutorial?.tags?.slice(0, 3)?.map((tag, index) => (
                <span key={index} className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                  {tag}
                </span>
              ))}
              {tutorial?.tags?.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{tutorial?.tags?.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Prerequisites */}
        {tutorial?.prerequisites && (
          <div className="mb-4">
            <div className="text-xs font-medium text-muted-foreground mb-2">Prerequisites:</div>
            <div className="space-y-1">
              {tutorial?.prerequisites?.slice(0, 2)?.map((prereq, index) => (
                <div key={index} className="flex items-center space-x-2 text-xs">
                  <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                  <span className="text-muted-foreground">{prereq}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Author and Date */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
          <div className="flex items-center space-x-2">
            <User className="w-3 h-3" />
            <span>{tutorial?.author}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="w-3 h-3" />
            <span>Updated {tutorial?.lastUpdated}</span>
          </div>
        </div>

        {/* Progress Bar (if user has started) */}
        {userProgress && progressPercentage > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-muted-foreground">Your Progress</span>
              <span className="font-semibold">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {userProgress?.completedLessons} of {tutorial?.lessons} lessons completed
            </div>
          </div>
        )}

        {/* Action Button */}
        <Button
          className="w-full flex items-center justify-center space-x-2"
          onClick={(e) => {
            e?.stopPropagation();
            onStart(tutorial);
          }}
        >
          <Play className="w-4 h-4" />
          <span>{userProgress?.completedLessons > 0 ? 'Continue Learning' : 'Start Tutorial'}</span>
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

export default TutorialCard;